/**
 * Vehicle trade data wizard additional data section
 *
 * @author Fabio Dal Santo <fabio@gestionaleauto.com>
 */

/**
 * Main
 */
import React, { useEffect, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { useTranslation } from 'react-i18next';
import { useFormik, setNestedObjectValues } from 'formik';
import HttpStatus from 'http-status-codes';
import * as Yup from 'yup';

/**
 * Redux
 */
import { useSelector, useDispatch } from 'react-redux';

/**
 * Material UI
 */
import { makeStyles } from '@mui/styles';
/**
 * Redux slices
 */
import { leadState, updateQuoteData, updateQuoteStepValidation } from '../../../redux/leadSlice';
import {
  initUpdate,
  setStepValidation,
  successUpdate,
  wizardState,
} from '../../../redux/wizardSlice';
import { lookupsState } from '../../../redux/lookupsSlice';
/**
 * Helpers
 */
import { PREVIOUS_OWNERS, tradeSubSteps } from '../../../helpers/const';
import Api from '../../../helpers/api';
import utils from '../../../helpers/utils';

const useStyles = makeStyles(() => ({
  radioWrapper: {
    textAlign: 'left',
    '&:after': {
      content: '" "',
      display: 'table',
      clear: 'both',
    },
  },
  isInvalid: {
    '& > input,select': {
      borderColor: '#d50000',
    },
    '& > .mdl-textfield__error': {
      visibility: 'visible',
    },
  },
}));

/**
 * @returns {JSX.Element}
 */
function Data() {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [quoteResults, setQuoteResults] = useState([]);
  const { quote, quoteSteps: steps } = useSelector(leadState);
  const { configuration } = useSelector(lookupsState);
  const { actionRequired, validateForm: needValidate } = useSelector(wizardState);
  const {
    stateMileage, techColor, statePreviousOwners, stateMaintenanceBook,
    versionId, stateRegistrationYear,
  } = quote;

  const getMaintenanceBookValue = () => {
    if (typeof stateMaintenanceBook !== 'boolean') {
      return null;
    }

    if (stateMaintenanceBook) {
      return 't';
    }

    return 'f';
  };

  const initialData = {
    mileage: typeof stateMileage === 'number' ? stateMileage.toString() : '',
    color: typeof techColor === 'string' ? techColor : '',
    previousOwners: typeof statePreviousOwners === 'string' ? statePreviousOwners : '',
    maintenanceBook: getMaintenanceBookValue(),
  };

  const formik = useFormik({
    enableReinitialize: true,
    validateOnMount: true,
    validateOnBlur: true,
    validateOnChange: true,
    validationSchema: Yup.object({
      mileage: Yup.number()
        .min(0, t('Please insert mileage.'))
        .typeError(t('Please insert mileage.'))
        .required(t('Please insert mileage.')),
    }),
    initialValues: initialData,
  });
  /**
   * Next step CTA
   */
  useEffect(() => {
    if (actionRequired) {
      dispatch(initUpdate());
      // Dispatch changes
      const {
        color, previousOwners, maintenanceBook, mileage,
      } = formik.values;

      // Find the market value based on kilometers
      let qValue = { price: { avg: 0 } };
      if (quoteResults.values && quoteResults.values.length) {
        for (let i = 0; i < quoteResults.values.length; i += 1) {
          if (quoteResults.values[i].from <= mileage && quoteResults.values[i].to >= mileage) {
            qValue = quoteResults.values[i];
          }
        }
      }
      let quoteValue = qValue?.price.avg;
      if (quoteValue && configuration.price_range) {
        if (Object.entries(configuration.price_range).length) {
          quoteValue = utils.quotePriceRange(configuration.price_range, quoteValue);
        }
      }
      dispatch(updateQuoteData({
        stateMileage: parseInt(mileage, 10),
        techColor: color,
        statePreviousOwners: previousOwners,
        stateMaintenanceBook: maintenanceBook === 't',
        quoteValue,
      }));

      dispatch(successUpdate());
    }
  }, [actionRequired]);

  /**
   * Update quote list values
   * @returns {Promise<void>}
   */
  const updateQuotation = async () => {
    if (stateRegistrationYear && versionId && versionId !== '0') {
      const res = await Api.get({
        entryPoint: `/quote/${stateRegistrationYear}/${versionId}/km/distribution`,
        pgp: true,
      });
      if (res.status === HttpStatus.OK && res.data.success && res.data.result.count > 0) {
        setQuoteResults(res.data.result);
      }
    }
  };

  useEffect(() => {
    async function validate() {
      if (needValidate) {
        const validationErrors = await formik.validateForm();
        if (Object.keys(validationErrors).length > 0) {
          await formik.setTouched(setNestedObjectValues(validationErrors, true));
        }
      }
    }
    validate();
  }, [needValidate]);
  useEffect(() => {
    if (formik.isValid !== steps[2].isValid) {
      dispatch(updateQuoteStepValidation(
        {
          idx: tradeSubSteps.mileage,
          isValid: formik.isValid,
        },
      ));
    }
    dispatch(setStepValidation(formik.isValid));
  }, [formik.isValid, formik.values]);
  useEffect(() => {
    // Get vehicle quote
    updateQuotation();
  }, []);
  const getFieldClasses = (fieldKey, isText = true) => {
    const fieldClasses = [
      'mdl-textfield',
      'mdl-js-textfield',
    ];

    if (isText) {
      fieldClasses.push('mdl-textfield--floating-label');
    }

    if (formik.values[fieldKey] !== '') {
      fieldClasses.push('is-dirty');
    }

    if (formik.errors[fieldKey] && needValidate) {
      fieldClasses.push(classes.isInvalid);
    }

    return fieldClasses.join(' ');
  };

  return (
    <div className="col-xs-12">
      <div
        className={getFieldClasses('mileage')}
        style={{ marginBottom: 24 }}
      >
        <input
          className="mdl-textfield__input"
          type="number"
          id="mileage"
          name="mileage"
          value={formik.values.mileage}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
        />
        <label className="mdl-textfield__label" htmlFor="mileage">
          {
            `${t('vdetail.v3.mainData.mileage').charAt(0).toUpperCase()}${t('vdetail.v3.mainData.mileage').slice(1)} *`
          }
        </label>
        {needValidate && (
          <span className="mdl-textfield__error">{(formik.errors.mileage) || ''}</span>
        )}
      </div>
      <div
        className={getFieldClasses('previousOwners', false)}
        style={{ marginBottom: 24 }}
      >
        <select
          id="previousOwners"
          name="previousOwners"
          className="ga-select"
          value={formik.values.previousOwners}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
        >
          <option value="">{t('Previous owners')}</option>
          {PREVIOUS_OWNERS.map((value) => (
            <option
              value={value}
              key={uuid()}
            >
              {value}
            </option>
          ))}
        </select>
      </div>
      <div
        className={getFieldClasses('color')}
        style={{ marginBottom: 24 }}
      >
        <input
          className="mdl-textfield__input"
          type="text"
          id="color"
          name="color"
          value={formik.values.color}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
        />
        <label className="mdl-textfield__label" htmlFor="color">
          {t('Vehicle color')}
        </label>
      </div>
      <div className={classes.radioWrapper}>
        {t('Usage and maintenance book?')}
        <br />
        <label
          className="styled-checkbox"
          htmlFor="maintenance-book-true"
          style={{ marginTop: 12, float: 'left' }}
        >
          {t('Yes')}
          <input
            id="maintenance-book-true"
            type="radio"
            value="t"
            name="maintenanceBook"
            onChange={formik.handleChange}
            defaultChecked={formik.values.maintenanceBook === 't'}
          />
          <span className="checkmark" />
        </label>
        <label
          className="styled-checkbox"
          htmlFor="maintenance-book-false"
          style={{ marginTop: 12, marginLeft: 35, float: 'left' }}
        >
          {t('No')}
          <input
            id="maintenance-book-false"
            type="radio"
            value="f"
            name="maintenanceBook"
            onChange={formik.handleChange}
            defaultChecked={formik.values.maintenanceBook === 'f'}
          />
          <span className="checkmark" />
        </label>
      </div>
    </div>
  );
}

export default Data;
